import { useEffect, useState } from 'react';
import {
  KI_CLAUDE_MODELLE,
  KI_EINSTELLBAR,
  type KiEinstellbar,
  type KiSchluesselSeite,
  type KiTestZiel,
  type KiVerbindungsTest,
  type KiWertStand,
} from '@beholder/shared/module/ki';
import { api } from '../../core/api';
import type { GlobalProps } from '../../core/modul';
import { Auswahl, Eingabe, Feld, Karte, Knopf, Laden, Meldung, datumZeit, useAktion, useDaten } from '../../core/ui';

const PFAD = '/api/modul/ki';

const FELDER: { name: Exclude<KiEinstellbar, 'CLAUDE_MODELL'>; titel: string; beispiel: string }[] = [
  { name: 'OLLAMA_URL', titel: 'Adresse der lokalen KI (Ollama)', beispiel: 'http://ollama:11434' },
  { name: 'OLLAMA_MODELL', titel: 'Modell der lokalen KI', beispiel: 'qwen3:14b' },
  { name: 'OLLAMA_BILDMODELL', titel: 'Bild-Modell (Fotos, Scans) – „aus“ schaltet die Texterkennung ab', beispiel: 'qwen3-vl:8b-instruct' },
  { name: 'SEARXNG_URL', titel: 'Adresse der lokalen Suche (SearXNG)', beispiel: 'http://searxng:8080' },
];

function herkunft(w: KiWertStand): string {
  if (w.quelle === 'beholder') return `Hier eingetragen${w.geaendertAm ? ` am ${datumZeit(w.geaendertAm)}` : ''}${w.geaendertVon ? ` von ${w.geaendertVon}` : ''}.`;
  if (w.quelle === 'env') return `Gilt gerade aus der .env: ${w.envWert}`;
  return w.wert ? `Eingebauter Standard: ${w.wert}` : 'Nicht eingerichtet.';
}

/** Projektübergreifende Seite /modul/ki – nur für den Hauptadmin: Claude-Schlüssel, Modelle und Adressen. */
export function SchluesselSeite(_p: GlobalProps) {
  const seite = useDaten(() => api.get<KiSchluesselSeite>(`${PFAD}/schluessel`), []);
  const s = seite.daten;
  const speichern = useAktion();
  const pruefen = useAktion();
  const [neu, setNeu] = useState<string | null>(null);
  const [werte, setWerte] = useState<Partial<Record<KiEinstellbar, string>>>({});
  const [test, setTest] = useState<Partial<Record<KiTestZiel, KiVerbindungsTest>>>({});
  const [meldung, setMeldung] = useState('');

  // Eingabefelder zeigen nur hier Eingetragenes; leer = .env bzw. Standard.
  useEffect(() => {
    if (!s) return;
    setWerte(Object.fromEntries(KI_EINSTELLBAR.map((n) => [n, s.werte[n].quelle === 'beholder' ? s.werte[n].wert : ''])));
  }, [s]);

  async function aktion(fn: () => Promise<KiSchluesselSeite>, text: string) {
    setMeldung('');
    const r = await speichern.ausfuehren(fn);
    if (r) {
      seite.setDaten(r);
      setMeldung(text);
    }
    return r;
  }

  async function schluesselSpeichern() {
    if (!neu) return;
    const r = await aktion(() => api.put<KiSchluesselSeite>(`${PFAD}/schluessel/claude`, { schluessel: neu }), 'Der Claude-Schlüssel ist gespeichert und gilt sofort.');
    if (r) setNeu(null);
  }

  async function schluesselEntfernen() {
    if (!s) return;
    const folge = s.claude.envVorhanden ? 'Danach gilt wieder der Schlüssel aus der .env.' : 'Danach ist Claude nicht mehr eingerichtet.';
    if (!window.confirm(`Den hier eingetragenen Claude-Schlüssel entfernen? ${folge}`)) return;
    await aktion(() => api.delete<KiSchluesselSeite>(`${PFAD}/schluessel/claude`), 'Der Claude-Schlüssel ist entfernt.');
  }

  async function werteSpeichern() {
    await aktion(() => api.put<KiSchluesselSeite>(`${PFAD}/einstellungen`, werte), 'Gespeichert – gilt ab der nächsten Anfrage.');
  }

  async function testen(ziel: KiTestZiel, mitNeuem = false) {
    setTest((t) => ({ ...t, [ziel]: undefined }));
    const r = await pruefen.ausfuehren(() =>
      api.post<KiVerbindungsTest>(`${PFAD}/schluessel/test`, { ziel, ...(mitNeuem && neu ? { schluessel: neu } : {}) }),
    );
    if (r) setTest((t) => ({ ...t, [ziel]: r }));
    void seite.neuLaden();
  }

  const testErgebnis = (ziel: KiTestZiel) => {
    const t = test[ziel];
    return t ? <Meldung art={t.ok ? 'ok' : 'fehler'}>{`${t.text} (${(t.dauerMs / 1000).toFixed(1)} s)`}</Meldung> : null;
  };

  if (!s) {
    return (
      <div className="seite schmal">
        <div className="seiten-kopf">
          <h1>Schlüssel</h1>
        </div>
        {seite.fehler ? <Meldung>{seite.fehler}</Meldung> : <Laden />}
      </div>
    );
  }

  const c = s.claude;
  const modell = s.werte.CLAUDE_MODELL;
  const modellAuswahl: { id: string; name: string }[] = KI_CLAUDE_MODELLE.map((m) => ({ id: m.id, name: m.name }));
  for (const id of [modell.envWert, werte.CLAUDE_MODELL ?? '']) {
    if (id && !modellAuswahl.some((m) => m.id === id)) modellAuswahl.push({ id, name: id });
  }
  const geaendert = KI_EINSTELLBAR.some((n) => (werte[n] ?? '') !== (s.werte[n].quelle === 'beholder' ? s.werte[n].wert : ''));

  return (
    <div className="seite schmal">
      <div className="seiten-kopf">
        <h1>Schlüssel</h1>
      </div>
      <p className="leise ki-schluessel-text">
        Zugangsdaten und Einstellungen der KI-Hilfe. Was hier eingetragen ist, gilt sofort und geht vor der <code>.env</code> auf dem Server;
        ohne Eintrag gilt weiter die <code>.env</code>. Nur du als Hauptadmin siehst diese Seite.
      </p>
      <Meldung>{speichern.fehler || pruefen.fehler}</Meldung>
      <Meldung art="ok">{meldung}</Meldung>

      <Karte titel="Claude (Anthropic)">
        <div className="stapel">
          <div>
            <strong>API-Schlüssel: </strong>
            {c.quelle === 'beholder' ? (
              <span>
                eingetragen am {datumZeit(c.eingetragenAm!)}
                {c.eingetragenVon ? ` von ${c.eingetragenVon}` : ''}
                {c.endung ? `, endet auf …${c.endung}` : ''}
              </span>
            ) : c.quelle === 'env' ? (
              <span>aus der .env{c.endung ? `, endet auf …${c.endung}` : ''}</span>
            ) : (
              <span className="leise">keiner – Claude ist nicht eingerichtet.</span>
            )}
          </div>
          {c.unlesbar && (
            <Meldung>
              Der hier eingetragene Schlüssel lässt sich mit dem jetzigen Tresor-Schlüssel (BEHOLDER_SCHLUESSEL) nicht mehr lesen. Bitte neu eintragen.
            </Meldung>
          )}
          {neu === null ? (
            <div className="reihe">
              <Knopf art="haupt" onClick={() => setNeu('')}>
                {c.quelle === 'beholder' ? 'Ersetzen' : 'Schlüssel eintragen'}
              </Knopf>
              {c.quelle === 'beholder' && (
                <Knopf art="gefahr" disabled={speichern.laeuft} onClick={schluesselEntfernen}>
                  Entfernen
                </Knopf>
              )}
              <Knopf disabled={pruefen.laeuft || !c.quelle} onClick={() => testen('claude')}>
                {pruefen.laeuft ? 'Wird getestet …' : 'Verbindung testen'}
              </Knopf>
            </div>
          ) : (
            <div className="stapel">
              <Feld
                titel={c.quelle === 'beholder' ? 'Neuer API-Schlüssel' : 'API-Schlüssel'}
                hinweis={
                  <>
                    Anlegen unter <code>console.anthropic.com</code> → API Keys. Wird verschlüsselt gespeichert und nie wieder angezeigt.
                  </>
                }
              >
                <Eingabe
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="sk-ant-…"
                  value={neu}
                  onChange={(e) => setNeu(e.target.value)}
                />
              </Feld>
              <div className="reihe">
                <Knopf art="haupt" disabled={speichern.laeuft || neu.trim().length < 20} onClick={schluesselSpeichern}>
                  Speichern
                </Knopf>
                <Knopf disabled={pruefen.laeuft || neu.trim().length < 20} onClick={() => testen('claude', true)}>
                  {pruefen.laeuft ? 'Wird getestet …' : 'Vorher testen'}
                </Knopf>
                <Knopf art="leise" onClick={() => setNeu(null)}>
                  Abbrechen
                </Knopf>
              </div>
            </div>
          )}
          {testErgebnis('claude')}
          <Feld titel="Claude-Modell" hinweis={herkunft(modell)}>
            <Auswahl value={werte.CLAUDE_MODELL ?? ''} onChange={(e) => setWerte((w) => ({ ...w, CLAUDE_MODELL: e.target.value }))}>
              <option value="">{modell.envWert ? `Wie in der .env (${modell.envWert})` : 'Standard (claude-opus-5-5)'}</option>
              {modellAuswahl.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Auswahl>
          </Feld>
        </div>
      </Karte>

      <Karte titel="Lokale KI und lokale Suche (optional)">
        <div className="stapel">
          {FELDER.map((f) => (
            <Feld key={f.name} titel={f.titel} hinweis={herkunft(s.werte[f.name])}>
              <Eingabe
                spellCheck={false}
                placeholder={s.werte[f.name].envWert || s.werte[f.name].wert || f.beispiel}
                value={werte[f.name] ?? ''}
                onChange={(e) => setWerte((w) => ({ ...w, [f.name]: e.target.value }))}
              />
            </Feld>
          ))}
          <p className="leise">Leer lassen = Wert aus der .env.</p>
          <div className="reihe">
            <Knopf disabled={pruefen.laeuft} onClick={() => testen('lokal')}>
              Lokale KI testen
            </Knopf>
            <Knopf disabled={pruefen.laeuft} onClick={() => testen('suche')}>
              Suche testen
            </Knopf>
          </div>
          {testErgebnis('lokal')}
          {testErgebnis('suche')}
        </div>
      </Karte>

      <div className="reihe ki-schluessel-speichern">
        <Knopf art="haupt" disabled={!geaendert || speichern.laeuft} onClick={werteSpeichern}>
          Einstellungen speichern
        </Knopf>
        {geaendert && <span className="leise">Ungespeicherte Änderungen (Claude-Modell bzw. Adressen)</span>}
      </div>

      <Karte titel="Verlauf">
        {s.verlauf.length ? (
          <ul className="verlauf">
            {s.verlauf.map((v, i) => (
              <li key={i}>
                <span className="leise klein">{datumZeit(v.am)}</span>
                <span>{v.text}</span>
                <span className="leise klein">{v.wer ?? ''}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="leise">Noch keine Änderungen.</span>
        )}
      </Karte>
    </div>
  );
}
